import axios from "axios";
import { currentFacility } from "@/helpers/facility/current-facility";
import { facilityReviewSuccessMsg } from "@/helpers/facility/facility-reviews-success-filter";
import router from "@/router";

const state = {
  pagination: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ["location_code"],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    mustSort: false,
    multiSort: false,
    totalItems: 0,
    rowsPerPageOptions: [25, 50, 75, 100]
  },
  facilitiesList: [],
  facilities: [],
  currentFacilityAttribute: null,
  currentFacility: {
    ...currentFacility
  }
};

const actions = {
  // This is different than getAllFacilities which only retrieves created facilities and deals w/ pagination.
  getFacilityList: ({ commit }) => {
    return axios.get(`/facilities-list`)
      .then((resp) => {
        const facilities = resp.data;
        const sortedFacilities = facilities.slice().sort(
          (a, b) => a.location_code.localeCompare(b.location_code));
        const finalizedFacilities = sortedFacilities.filter(facility => facility.location_code !== "LTRAIN");

        process.env.VUE_APP_HIDE_LTRAIN === "true"
          ? commit("setFacilitiesList", finalizedFacilities)
          : commit("setFacilitiesList", sortedFacilities);
        return resp;
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  getAllFacilities: ({ commit, dispatch, state }, payload) => {
    const sortingOrder = state.pagination.sortDesc[0] ? "desc" : "asc";
    return axios.get(`/facilities`, {
      params: {
        per_page: payload && payload.per_page ? payload.per_page : state.pagination.itemsPerPage,
        page: state.pagination.page,
        sort: state.pagination.sortBy[0],
        order: sortingOrder
      }
    })
      .then((resp) => {
        commit("getAllFacilitiesSuccess", resp.data.facilities);
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", "Unable to retrieve facilities", { root: true });
        return Promise.reject(err);
      });
  },
  createFacility: ({ commit, dispatch, state }) => {
    return axios.post(`/facilities`, state.currentFacility)
      .then((resp) => {
        commit("resetCurrentFacility");
        dispatch("snackbar/successMessage", `Successfully created facility`, { root: true });
        router.push("/facilities");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  getFacility: ({ commit, dispatch }, id) => {
    return axios.get(`/facilities/${id}`)
      .then((resp) => {
        commit("setCurrentFacilityData", resp.data.facility);
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  updateFacility: ({ state, dispatch }) => {
    return axios.patch(`/facilities/${state.currentFacility.id}`, state.currentFacility)
      .then((resp) => {
        dispatch("snackbar/successMessage", `Successfully updated facility`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  uploadFacilityImage: ({ state, dispatch }, formData) => {
    return axios.post(`/facilities/${state.currentFacility.id}/images`, formData)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        dispatch("snackbar/successMessage", `Successfully uploaded facility image`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  uploadFacilityPromotionImage: ({ state, dispatch }, formData) => {
    return axios.post(`/facilities/${state.currentFacility.id}/promotion-image`, formData)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        dispatch("snackbar/successMessage", `Successfully uploaded facility promotion image`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  updateOgImage: ({ state, dispatch }, formData) => {
    return axios.post(`/facilities/${state.currentFacility.id}/og-image`, formData)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        dispatch("snackbar/successMessage", `Successfully uploaded OG image`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  deleteFacility: ({ dispatch }, id) => {
    return axios.delete(`/facilities/${id}`)
      .then((resp) => {
        dispatch("getAllFacilities");
        dispatch("snackbar/successMessage", `Successfully deleted facility`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  updateReview: ({ dispatch, state }, payload) => {
    return axios.patch(`/facility-reviews/${payload.id}`, payload)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        dispatch("snackbar/successMessage", facilityReviewSuccessMsg(payload), { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  deleteReview: ({ dispatch }, id) => {
    return axios.delete(`/facility-reviews/${id}`)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        dispatch("snackbar/successMessage", `Successfully deleted review`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  publishReview: ({ dispatch, state }, payload) => {
    return axios.patch(`/facility-reviews/${payload.id}/enable`, payload)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        dispatch("snackbar/successMessage", `Successfully displayed review`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  removeReview: ({ dispatch, state }, payload) => {
    return axios.patch(`/facility-reviews/${payload.id}/disable`, payload)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        dispatch("snackbar/successMessage", `Successfully hidden review`, { root: true });
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },
  uploadFacilityUnits: ({ dispatch, state }, formData) => {
    return axios.post(`/facilities/${state.currentFacility.id}/units`, formData)
      .then((resp) => {
        dispatch("getFacility", state.currentFacility.id);
        resolve(resp);
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        return Promise.reject(err);
      });
  },

  addLink: ({ commit }) => {
    commit("addLink");
  },

  removeLink: ({ commit }, key) => {
    commit("removeLink", key);
  },

  updateIcon: ({ commit }, payload) => {
    commit("updateIcon", payload);
  },

  updateUri: ({ commit }, payload) => {
    commit("updateUri", payload);
  }

};

const mutations = {
  setFacilitiesList: (state, payload) => {
    state.facilitiesList = payload;
  },
  setFacilityPagination: (state, payload) => {
    state.pagination = payload;
  },
  getAllFacilitiesSuccess: (state, facilitiesResponse) => {
    state.facilities = facilitiesResponse.data;
    state.pagination.totalItems = facilitiesResponse.total;
  },
  setCurrentFacilityData: (state, payload) => {
    state.currentFacility = {
      ...state.currentFacility,
      ...payload
    };

    if (payload.unit_attribute_map && payload.unit_attribute_map.map) {
      state.currentFacility.unit_attribute_map = payload.unit_attribute_map.map;
    } else {
      state.currentFacility.unit_attribute_map = {};
    }
  },
  setCurrentFacilityLatLng: (state, payload) => {
    state.currentFacility.latitude = payload.lat;
    state.currentFacility.longitude = payload.lng;
  },
  setCurrentFacilityUnitAttributeMap: (state, payload) => {
    if (state.currentFacilityAttribute) {
      state.currentFacility.unit_attribute_map[state.currentFacilityAttribute] = payload;
    } else {
      state.currentFacility.unit_attribute_map[Object.keys(payload)[0]] = payload[Object.keys(payload)[0]];
    }
  },
  setCurrentFacilityAttribute: (state, payload) => {
    return new Promise(resolve => {
      state.currentFacilityAttribute = payload;
      resolve();
    });
  },
  clearPromoImage: (state) => {
    state.currentFacility.promotion_image = null;
  },
  clearPromoMobileImage: (state) => {
    state.currentFacility.promotion_mobile_image = null;
  },
  clearOgImage: (state) => {
    state.currentFacility.og_image = null;
  },
  resetCurrentFacility: (state) => {
    Object.keys(state.currentFacility).forEach((value, index) => {
      if (value == "access_hours" || value == "office_hours") {
        return;
      }

      if (value == "promotion_enabled" || value == "published") {
        state.currentFacility[value] = false;
        return;
      }

      state.currentFacility = { ...currentFacility };
    });
    state.currentFacility.pms = [];
  },

  addLink: (state) => {
    state.currentFacility.social_links.push({
      uri: "",
      icon: ""
    });
  },

  removeLink: (state, key) => {
    state.currentFacility.social_links.splice(key, 1);
  },

  updateUri: (state, payload) => {
    state.currentFacility.social_links[payload.index].uri = payload.uri;
  },

  updateIcon: (state, payload) => {
    state.currentFacility.social_links[payload.index].icon = payload.icon;
  }
};

export default {
  state,
  actions,
  mutations
};
